// src/services/commodity/CommodityService.ts
import { BaseTenantCrudService } from '@/services/_base/BaseTenantCrudService'
import { type CommodityType } from '@/types/CommodityType'
import type { IFirestoreService } from '@/platform/firebase/firestore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export class CommodityService extends BaseTenantCrudService<CommodityType> {
  constructor(firestore: IFirestoreService, session: Session) {
    super('commodity_types', firestore, session)
  }

  protected override beforeCreate(data: Partial<CommodityType>): Partial<CommodityType> {
    return {
      ...super.beforeCreate(data),
      is_active: data.is_active ?? true,
      is_deleted: data.is_deleted ?? false,
      is_favorite: data.is_favorite ?? false,
      current_price_per_lb: data.current_price_per_lb ?? 0,
    }
  }

  protected override beforeUpdate(id: string, data: Partial<CommodityType>): Partial<CommodityType> {
    return {
      ...super.beforeUpdate(id, data),
    }
  }

  /** Get all active commodity types */
  async getActiveCommodityTypes(): Promise<CommodityType[]> {
    const results = await this.firestore.runQuery({
      collection: this.getCollectionPath(),
      wheres: [
        { field: 'is_active', op: '==', value: true },
        { field: 'is_deleted', op: '==', value: false },
      ],
      orders: [{ field: 'name', dir: 'asc' }],
      meta: { name: 'commodity_types.active' },
    })

    return results as CommodityType[]
  }

  /** Soft delete a commodity type */
  async softDelete(id: string): Promise<void> {
    await this.update(id, {
      is_deleted: true,
      is_active: false,
    } as Partial<CommodityType>)
  }

  /** Toggle favorite status */
  async toggleFavorite(id: string): Promise<void> {
    const commodity = await this.get(id)
    if (commodity) {
      await this.update(id, {
        is_favorite: !commodity.is_favorite,
      } as Partial<CommodityType>)
    }
  }
}

